"use client";

import React, { useCallback } from "react";
import { Check } from "lucide-react";

import { cn } from "@/lib/utils";
import { LAYOUT_OPTIONS, LayoutId } from "@/lib/layouts";

interface LayoutGridProps {
  value: LayoutId;
  onChange: (layout: LayoutId) => void;
}

/**
 * Schematic of each arrangement, drawn from boxes rather than a screenshot so
 * it cannot go stale against the real shell. Decorative — the name and
 * description carry the meaning for screen readers.
 *
 * Exported for the Customise drawer, which draws the same four thumbnails in a
 * narrower two-column grid. One drawing, so a layout can never be pictured two
 * different ways.
 */
export function LayoutDiagram({ id }: { id: LayoutId }): React.ReactElement {
  const bar = "rounded-sm bg-muted-foreground/30";
  const pane = "rounded-sm bg-muted-foreground/15";

  if (id === "reader") {
    // A nav band edge to edge, then a CENTRED reading column with gutters
    // either side — the shell centres both the bar and the column to the same
    // measure, and the drawing said full-bleed, which is the one thing this
    // layout is not. No side column at all is what separates it from Spaces.
    return (
      <div aria-hidden className="flex h-14 w-full flex-col gap-1 p-1">
        <div className={cn(bar, "h-2 w-full")} />
        <div className="flex flex-1 justify-center">
          <div className={cn(pane, "h-full w-3/5")} />
        </div>
      </div>
    );
  }

  if (id === "spaces") {
    // One sidebar flush left, then a NARROW centred column. The visible
    // gutters either side are the whole point — they are what separates this
    // from Standard at thumbnail size, where a 6px icon rail is invisible.
    return (
      <div aria-hidden className="flex h-14 w-full gap-1 p-1">
        <div className={cn(bar, "h-full w-1/4")} />
        <div className="flex flex-1 justify-center">
          <div className={cn(pane, "h-full w-1/2")} />
        </div>
      </div>
    );
  }

  if (id === "console") {
    // Two panes only: one WIDE sidebar and a detail pane filling everything
    // else. No icon rail and no third column — that absence is what tells it
    // apart from Standard, and the full-width pane is what separates it from
    // Spaces, whose column is constrained to a reading measure.
    return (
      <div aria-hidden className="flex h-14 w-full gap-1 p-1">
        <div className={cn(bar, "h-full w-2/5")} />
        <div className={cn(pane, "h-full flex-1")} />
      </div>
    );
  }

  // rail — icon rail, sidebar, one content column.
  //
  // The icon rail is drawn at w-3 rather than its true proportion (60px of
  // ~1400 would be a hairline here). Two distinct left strips is the shape
  // that tells Standard apart from Spaces, so it has to survive the
  // thumbnail.
  return (
    <div aria-hidden className="flex h-14 w-full gap-1 p-1">
      <div className={cn(bar, "h-full w-3 shrink-0")} />
      <div className={cn(bar, "h-full w-1/4")} />
      <div className={cn(pane, "h-full flex-1")} />
      {/* The trending column. Console drops this; drawing it here is what
          makes the two cards tell apart at thumbnail size. */}
      <div className={cn(bar, "h-full w-1/6")} />
    </div>
  );
}

const LayoutCard = React.memo(function LayoutCard({
  id,
  name,
  description,
  isSelected,
  onSelect,
}: {
  id: LayoutId;
  name: string;
  description: string;
  isSelected: boolean;
  onSelect: (id: LayoutId) => void;
}): React.ReactElement {
  const handleClick = useCallback(() => {
    onSelect(id);
  }, [id, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isSelected}
      className={cn(
        "group flex flex-col gap-2 rounded-xl border-2 p-4 text-left transition-all",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isSelected
          ? "border-primary bg-primary/5"
          : "border-border bg-card hover:border-primary/40",
      )}
    >
      <div className="relative overflow-hidden rounded-md border border-border bg-background">
        <LayoutDiagram id={id} />
        {isSelected && (
          <div className="absolute inset-0 flex items-center justify-center bg-primary/10">
            <Check size={18} className="text-primary drop-shadow-sm" />
          </div>
        )}
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{name}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  );
});

export const LayoutGrid = React.memo(function LayoutGrid({
  value,
  onChange,
}: LayoutGridProps): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {LAYOUT_OPTIONS.map((option) => (
        <LayoutCard
          key={option.id}
          id={option.id}
          name={option.name}
          description={option.description}
          isSelected={value === option.id}
          onSelect={onChange}
        />
      ))}
    </div>
  );
});
